import { Delete, DragIndicator } from "@mui/icons-material";
import { Box, Button, IconButton, List, ListItem, Tooltip } from "@mui/material";
import { Cell } from "generated/graphql";
import { swapElements } from "helpers/drag-and-drop";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import { FC, useContext, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { useAppSelector } from "redux/hooks";
import {
  deleteCell,
  selectLensEditorCellAiConfigs,
  selectLensEditorForm,
  selectLensEditorLensId,
  selectProfileBuilderAiMode,
  selectProfileBuilderFieldById,
  updateCells,
} from "redux/slices/lensEditor";
import theme from "theme";
import { P } from "theme/typography";
import useAppDispatchWithSnackbar from "../../../helpers/hooks/useAppDispatchWithSnackbar";
import CellPreview from "./CellPreview";
import NewCellDialog from "routes/lens-editor/profile-builder/NewCellDialog";
import { LensUpdateSnackbarContext } from "contexts/LensUpdateSnackbarContext";

interface CellsProps {
  fieldId: number;
  cells: Cell[];
  handleCellClick: (cell: Cell) => void;
}

const Cells: FC<CellsProps> = ({ fieldId, cells, handleCellClick }) => {
  const form = useAppSelector(selectLensEditorForm);
  const snackpatch = useAppDispatchWithSnackbar();
  const cellAiConfigs = useAppSelector(selectLensEditorCellAiConfigs);
  const aiMode = useAppSelector(selectProfileBuilderAiMode);

  const [showNewCellDialog, setShowNewCellDialog] = useState(false);
  const selectedField = useAppSelector((state) => selectProfileBuilderFieldById(state, fieldId));

  const lensId = useAppSelector(selectLensEditorLensId);
  const { enqueueLensUpdateTimerSnackbar } = useContext(LensUpdateSnackbarContext);

  const handleDeleteCell = (fieldId: number, cellId: number) => {
    void snackpatch(deleteCell({ cellId, fieldId }), {
      successMessage: "Cell deleted",
      errorMessage: "Unable to delete cell",
    }).then(() => enqueueLensUpdateTimerSnackbar(lensId));
  };

  const onDragEnd = (result: DropResult, cells: Cell[]) => {
    if (!result.destination) {
      return;
    }

    const reorderedCells = swapElements<Cell>(cells, result.source.index, result.destination.index);
    void snackpatch(
      updateCells({
        cells: reorderedCells.map((c, i) => ({
          cellId: c.id,
          values: {
            rank: i,
          },
        })),
      }),
      { successMessage: "Cells reordered successfully", errorMessage: "Unable to reorder cells" }
    ).then(() => enqueueLensUpdateTimerSnackbar(lensId));
  };

  const filteredCells = useMemo(() => {
    const sorted = cells.slice().sort((a, b) => a.rank - b.rank);
    const aiOnly = sorted.filter((cell) => !cellAiConfigs.find((c) => c.cellId === cell.id)?.aiExclude);
    return aiMode ? aiOnly : sorted;
  }, [cells, cellAiConfigs, aiMode]);

  const confirmCtx = useContext(ConfirmationDialogContext);

  if (filteredCells.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
          py: 8,
        }}>
        <P variant="xl2Bold">Empty Field</P>
        <Button
          variant="outlined"
          onClick={() => {
            setShowNewCellDialog(true);
          }}>
          Create a new cell
        </Button>
        {showNewCellDialog && selectedField && (
          <NewCellDialog
            open={showNewCellDialog}
            close={() => setShowNewCellDialog(false)}
            field={selectedField}
            rank={selectedField.cells.length}
          />
        )}
      </Box>
    );
  }

  return (
    <DragDropContext onDragEnd={(result) => onDragEnd(result, filteredCells)}>
      <Droppable droppableId="cellDroppable" isDropDisabled={aiMode}>
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={{
              background: snapshot.isDraggingOver ? theme.palette.grey[100] : "white",
              flexGrow: 1,
            }}>
            <List
              sx={{
                p: 0,
              }}>
              {filteredCells.map((cell, index) => (
                <Draggable key={cell.id} draggableId={`${cell.id}`} index={index} isDragDisabled={aiMode}>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}>
                      <Box
                        sx={{
                          borderBottom: index === cells.length - 1 ? "none" : `1px solid ${theme.palette.grey[100]}`,
                          backgroundColor: snapshot.isDragging ? theme.palette.primary.light : "white",
                          borderRadius: snapshot.isDragging ? 1 : 0,
                          display: "flex",
                          alignItems: "center",
                          p: 1,
                        }}>
                        {filteredCells.length > 1 && !aiMode && (
                          <IconButton size="small">
                            <span {...provided.dragHandleProps}>
                              <DragIndicator />
                            </span>
                          </IconButton>
                        )}

                        <ListItem>
                          <CellPreview
                            cell={cell}
                            onClick={() => {
                              handleCellClick(cell);
                            }}
                            cellAiConfig={cellAiConfigs.find((c) => c.cellId === cell.id)}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              position: "absolute",
                              right: 16,
                              top: 0,
                            }}>
                            {!form.live ? (
                              <IconButton
                                onClick={() => {
                                  confirmCtx.confirm({
                                    title: "Delete Cell",
                                    prompt: (
                                      <P variant="body4">
                                        Are you sure you want to delete cell <b>{cell.name}</b> ?
                                      </P>
                                    ),
                                    callback: () => {
                                      return Promise.resolve(handleDeleteCell(fieldId, cell.id));
                                    },
                                  });
                                }}>
                                <Delete />
                              </IconButton>
                            ) : (
                              <Tooltip title="Cannot delete a cell from a live form">
                                <span>
                                  <IconButton disabled>
                                    <Delete />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </ListItem>
                      </Box>
                    </div>
                  )}
                </Draggable>
              ))}
            </List>
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default Cells;
